import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { Badge, Card, PageHeader, Skeleton, Table } from "@/components/ui";

interface CommunicationLogEntry {
  id: string;
  notification_type: string;
  /**
   * The human name of the event, from the API. This screen used to keep its own
   * map of type to label, which silently printed the raw "account.created" for
   * any type nobody remembered to add -- so the names live in one language now,
   * beside the types themselves.
   */
  label?: string;
  recipient_kind?: string;
  recipient_email: string;
  subject: string;
  status: "sent" | "failed";
  error?: string;
  shipment_id?: string;
  created_at: string;
}

/**
 * Every email this account has actually been sent, or that failed to send.
 *
 * A failed send stays on the list rather than being hidden: knowing a
 * booking confirmation never arrived is exactly what a customer wondering
 * "did that even go through" needs to see.
 */
export function Communications() {
  const { data, isLoading } = useQuery({
    queryKey: ["communications"],
    queryFn: () => api.get<{ data: CommunicationLogEntry[] }>("/v1/communications"),
  });

  return (
    <>
      <PageHeader title="Communications" subtitle="Every notification this account has been sent by email." />

      {isLoading || !data ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : data.data.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-500">Nothing has been sent yet.</p>
        </Card>
      ) : (
        <Table head={["When", "Notification", "Sent to", "Subject", "Status"]}>
          {data.data.map((entry) => (
            <tr key={entry.id}>
              <td className="ws-td whitespace-nowrap text-ink-500">{dateTime(entry.created_at)}</td>
              <td className="ws-td">{entry.label || entry.notification_type}</td>
              <td className="ws-td text-ink-600">{entry.recipient_email}</td>
              <td className="ws-td max-w-xs truncate">{entry.subject}</td>
              <td className="ws-td">
                <Badge tone={entry.status === "sent" ? "good" : "bad"}>{entry.status}</Badge>
                {entry.status === "failed" && entry.error && (
                  <p className="mt-1 text-xs text-ink-400">{entry.error}</p>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
